import { Roles } from '@consts/role';
import { RcFile } from 'antd/es/upload';
import { ChildInfo } from './chat';
import { Membership } from './membership';
import { Permission } from '@consts/permissions';

export enum LoginType {
  EMAIL = 'email',
  KAKAO = 'kakao',
  NAVER = 'naver',
  APPLE = 'apple',
}
export enum ProfileApprovalState {
  WAIT = 'waitApproval',
  CONFIRM = 'confirmApproval',
  NOTHING = 'beforeApproval',
  DENIED = 'refuseApproval',
}

export interface Address {
  postcode?: string;
  postcode1?: string;
  postcode2?: string;
  postcodeSeq?: string;
  zonecode?: string;
  address?: string;
  addressEnglish?: string;
  addressType?: string;
  bcode?: string;
  bname?: string;
  bnameEnglish?: string;
  bname1?: string;
  bname1English?: string;
  bname2?: string;
  bname2English?: string;
  sido?: string;
  sidoEnglish?: string;
  sigungu?: string;
  sigunguEnglish?: string;
  sigunguCode?: string;
  userLanguageType?: string;
  query?: string;
  buildingName?: string;
  buildingCode?: string;
  apartment?: string;
  jibunAddress?: string;
  jibunAddressEnglish?: string;
  roadAddress?: string;
  roadAddressEnglish?: string;
  autoRoadAddress?: string;
  autoRoadAddressEnglish?: string;
  autoJibunAddress?: string;
  autoJibunAddressEnglish?: string;
  userSelectedType?: string;
  noSelected?: string;
  hname?: string;
  roadnameCode?: string;
  roadname?: string;
  roadnameEnglish?: string;
  address2?: string;
}
export interface AttachImage {
  id: string;
  url: string;
  file?: RcFile;
  originFileName?: string;
  contentType?: string;
}

export interface TutorContract {
  contractType: string;
  contractPhoto?: string;
  licensePhoto?: string | AttachImage[];
  bankName?: string;
  bankAccountHolder?: string;
  bankAccountNumber?: string;
  bankAccountPhoto?: string | AttachImage[];
  contractEmail?: string;
  businessName?: string;
  representative?: string;
  address?: any;
  businessNumber?: string;
}

export interface UserProfile {
  name?: string;
  nickname?: string;
  message?: string;
  realName?: string;
  educationArea?: any;
  agreement?: boolean;
  homepageId?: string;
  educationTitle?: string;
  imageUrl?: string;
  phone?: string;
  positiveWeeks?: string[];
  favorites: string[];
  contract?: TutorContract;
  creditPoints?: number;
  coupons?: string[];
  otherAlarmAgree?: boolean;
  marketingEmailAgree?: boolean;
  marketingSMSAgree?: boolean;
  children?: ChildInfo[];
  address?: Address;
  categorySubList?: string[];
  active?: boolean;
  activeAt?: string;
  customerServicePhone?: string; // for seller
  customerServiceUrl?: string; // for seller
  mainCustomerService?: string; // for seller
  contractPhoto?: string;
  licensePhoto?: string;
  bankAccountPhoto?: string;
  commissionRate?: string;
  commissionRateId?: string;
  contractStatus?: string;
  salesChannels?: string[];
  contentWrite?: {
    denied: boolean;
    deniedReason?: string;
    deniedUntil?: Date;
  };
}

export interface SelectedDistricts {
  city?: string;
  district?: string;
}

export interface PartnerProfile {
  intro?: string; //greeting
  profileMainImageUrl?: string;
  introImages?: string[];
  /** 업력 시작 연월. YYYY-MM */
  activityStartYearMonth?: string | null;
  academics?: Academic[];
  careers?: Career[];
  certificates?: Certificate[];
  mainActivityZone?: SelectedDistricts[];
  updatedAt?: Date;
}

export interface Academic {
  degree?: string;
  school?: string;
  graduation?: string;
  year?: string;
  imageUrl?: string;
  approvalState?: ProfileApprovalState;
  rejectedReasonMessage?: string;
}

export interface Career {
  career?: string;
  startYearMonth?: string; // 2024-09 > migration api
  endYearMonth?: string;
  imageUrl?: string;
  approvalState?: ProfileApprovalState;
  description?: string;
  period?: string;
}

export interface Certificate {
  name?: string;
  issuer?: string;
  issuanceYearMonth?: string;
  imageUrl?: string;
  approvalState?: ProfileApprovalState;
}

export interface PartnerStatistics {
  review?: {
    partner_id?: string;
    review_count?: number;
    sentiment_analysis?: {
      average_rating?: number;
      total_reviews?: number;
      positive_reviews?: number;
      negative_reviews?: number;
      neutral_reviews?: number;
      positive_ratio?: number;
    };
    model_performance?: {
      precision?: number;
      recall?: number;
      f1_score?: number;
      accuracy?: number;
      note?: string;
      distribution?: {
        positive_ratio?: number;
        negative_ratio?: number;
        neutral_ratio?: number;
        balance_score?: number;
      };
    };
    calculated_score?: number;
    score_breakdown?: {
      rating_score?: number;
      positive_score?: number;
      volume_score?: number;
    };
    analysis_status?: string;
    updatedAt?: string;
  };
  chat?: {
    partner_id?: string;
    total_messages?: number;
    sentiment_distribution?: {
      positive?: number;
      neutral?: number;
      negative?: number;
    };
    positive_ratio?: number;
    analyzed_texts?: number;
    calculated_score?: number;
    score_breakdown?: {
      positive_score?: number;
      volume_score?: number;
      balance_score?: number;
    };
    model_performance?: {
      precision?: number;
      recall?: number;
      f1_score?: number;
      accuracy?: number;
      note?: string;
      distribution?: {
        positive_ratio?: number;
        negative_ratio?: number;
        neutral_ratio?: number;
        balance_score?: number;
      };
    };
    updatedAt?: string;
  };
  product?: {
    open_count?: number;
    total_count?: number;
    updated_at?: string;
  };
  report?: {
    partner_id: string;
    total_reports: number;
    content_score: number;
    comment_score: number;
    intelligences_score: number;
    images_score: number;
    score_breakdown: {
      comment: {
        total: number;
        max_score: number;
        weight: string;
      };
      intelligences: {
        total: number;
        max_score: number;
        weight: string;
      };
      images: {
        total: number;
        max_score: number;
        weight: string;
      };
    };
    analysis_details: {
      reports_analyzed: Array<{
        report_id: string;
        comment_score: number;
        comment_breakdown: {
          required_elements: {
            total: number;
            max_score: number;
            elements: {
              activity_participation: {
                score: number;
                max_score: number;
                description: string;
                matched_keywords: string[];
                achieved: boolean;
              };
              individual_characteristics: {
                score: number;
                max_score: number;
                description: string;
                matched_keywords: string[];
                achieved: boolean;
              };
              growth_changes: {
                score: number;
                max_score: number;
                description: string;
                matched_keywords: string[];
                achieved: boolean;
              };
              specific_examples: {
                score: number;
                max_score: number;
                description: string;
                matched_keywords: string[];
                achieved: boolean;
              };
            };
          };
          quality_evaluation: {
            total: number;
            max_score: number;
            criteria: {
              specificity: {
                score: number;
                max_score: number;
                description: string;
                matched_indicators: string[];
              };
              professionalism: {
                score: number;
                max_score: number;
                description: string;
                matched_indicators: string[];
              };
              personalization: {
                score: number;
                max_score: number;
                description: string;
                matched_indicators: string[];
              };
            };
          };
          volume_bonus: number;
          total_score: number;
          max_score: number;
        };
        comment_details: {
          length: number;
          length_requirement: string;
          length_status: string;
          element_analysis: {
            activity_participation: {
              score: number;
              max_score: number;
              description: string;
              matched_keywords: string[];
              achieved: boolean;
            };
            individual_characteristics: {
              score: number;
              max_score: number;
              description: string;
              matched_keywords: string[];
              achieved: boolean;
            };
            growth_changes: {
              score: number;
              max_score: number;
              description: string;
              matched_keywords: string[];
              achieved: boolean;
            };
            specific_examples: {
              score: number;
              max_score: number;
              description: string;
              matched_keywords: string[];
              achieved: boolean;
            };
          };
          quality_analysis: {
            specificity: {
              score: number;
              max_score: number;
              description: string;
              matched_indicators: string[];
            };
            professionalism: {
              score: number;
              max_score: number;
              description: string;
              matched_indicators: string[];
            };
            personalization: {
              score: number;
              max_score: number;
              description: string;
              matched_indicators: string[];
            };
          };
          overall_assessment: {
            excellent: boolean;
            good: boolean;
            fair: boolean;
            poor: boolean;
          };
        };
        intelligences_score: number;
        intelligences_details: {
          error: string;
        };
        images_score: number;
        images_details: {
          image_count: number;
          valid_images: string[];
          score: number;
          max_score: number;
          rating: string;
          score_criteria: {
            [key: string]: string;
          };
        };
        total_score: number;
      }>;
      evaluation_criteria: {
        comment: string;
        intelligences: string;
        images: string;
      };
    };
    updatedAt: Date;
  };
  summary?: {
    comprehensive_score?: number;
    precision_score?: {
      overall_score?: number;
      components?: {
        review?: {
          precision?: number;
          recall?: number;
          f1_score?: number;
          accuracy?: number;
          balance_score?: number;
          calculated_score?: number;
        };
        chat?: {
          model_performance?: {
            precision?: number;
            f1_score?: number;
            accuracy?: number;
            calculated_score?: number;
          };
          heuristic?: {
            volume_score?: number;
            balance_score?: number;
            total_messages?: number;
            positive_ratio?: number;
            calculated_score?: number;
          };
          final_score?: number;
        };
      };
      data_quality_score?: number;
      bonus_score?: number;
      base_score?: number;
      evaluation_note?: string;
    };
    score_breakdown?: {
      review?: {
        score?: number;
        weight?: number;
        contribution?: number;
      };
      chat?: {
        score?: number;
        weight?: number;
        contribution?: number;
      };
      content?: {
        score?: number;
        weight?: number;
        contribution?: number;
      };
      timing?: {
        score?: number;
        weight?: number;
        contribution?: number;
      };
    };
    updatedAt?: string;
  };
}

export interface User {
  id: string;
  password?: string;
  email: string;
  loginType: LoginType;
  role: Roles;
  roles?: Roles[];
  authorizations?: Permission[]; // 권한 배열
  profile?: UserProfile;
  partnerProfile?: PartnerProfile;
  statistics?: PartnerStatistics;
}

export interface ExcellentPartnerMetrics {
  reviewAvg3m: number | null;
  reviewCount3m: number;
  reviewCountAll: number;
  sales3m: number;
  refund3m: number;
  partnerFaultCount3m?: number;
  partnerFaultRate3m?: number | null;
  reservationSuccessRate3m?: number | null;
  reservationSuccessPercentile?: number | null;
  reservationSuccessCutoff?: number | null;
  salesCount1m?: number;
  salesPercentile?: number | null;
  salesCountCutoff?: number | null;
  satisfactionScore: number;
  reviewCountScore: number;
  reservationSuccessScore: number;
  salesScore: number;
}

export interface ExcellentPartner {
  selected: boolean;
  manualPinned: boolean;
  source?: 'auto' | 'manual' | null;
  selectedAt?: string | null;
  retainedUntil?: string | null;
  evaluatedAt?: string;
  score: number;
  criteria?: {
    satisfaction: boolean;
    recentReviewCount?: boolean;
    reviewCount: boolean;
    reservationSuccess: boolean;
    sales: boolean;
  };
  metrics?: ExcellentPartnerMetrics;
}

export interface AdminUser {
  _id: string;
  password?: string;
  emails: { address: string; verified?: boolean }[];
  loginType: LoginType;
  roles: Roles[];
  profile?: UserProfile;
  partnerProfile?: PartnerProfile;
  membership?: Membership;
  partners?: { id: string; nickname: string }[];
  excellentPartner?: ExcellentPartner;
  createdAt: Date;
  deletedAt?: Date;
  loginMethods?: {
    email?: { address: string; hasPassword: boolean };
    kakao?: { id: string; email?: string };
    naver?: { id: string; email?: string };
    apple?: { id: string; email?: string };
  };
  pendingEmailLogin?: { email: string; expiresAt: string } | null;
  services?: {
    kakao?: { id?: string; email?: string };
    naver?: { id?: string; email?: string };
    apple?: { id?: string; email?: string };
    password?: { exists?: boolean };
  };
}

export interface CreateSellerUserBody {
  type: 'email';
  email?: string;
  password?: string;
  profileImage?: string;
  name?: string;
  roles?: Roles[];
  nickname?: string;
  phoneNumber?: string;
  customerServicePhone?: string;
  customerServiceUrl?: string;
  mainCustomerService?: string;
  contractPhotoTemp?: string;
  contractPhoto?: string;
  licensePhotoTemp?: string;
  licensePhoto?: string;
  bankAccountPhotoTemp?: string;
  bankAccountPhoto?: string;
  commissionRate?: string;
  commissionRateId?: string;
  contractStatus?: string;
  salesChannels?: string[];
}

export interface UpdateUserBody {
  type?: string;
  email?: string;
  password?: string;
  profileImage?: string;
  name?: string;
  nickname?: string;
  message?: string;
  realName?: string;
  phoneNumber?: string;
  customerServicePhone?: string;
  customerServiceUrl?: string;
  mainCustomerService?: string;
  contractPhotoTemp?: string;
  contractPhoto?: string;
  licensePhotoTemp?: string;
  licensePhoto?: string;
  bankAccountPhotoTemp?: string;
  bankAccountPhoto?: string;
  commissionRate?: string;
  commissionRateId?: string;
  contractStatus?: string;
  salesChannels?: string[];
  roles?: Roles[];
  membership?: Membership;
  contentWrite?: {
    denied: boolean;
    deniedReason?: string;
    deniedUntil?: Date;
  };
}
